import { detCos, detSin } from '../../engine/simulation/fixed';
import { Rng, hash2 } from '../../engine/simulation/rng';
import { RESOURCE_BUDGET, Terrain, type GameMap, type MapType, type ResourceSpawn } from './mapTypes';

/**
 * Deterministic procedural map generator. Uses only integer hashing and IEEE
 * basic arithmetic so identical seeds yield identical maps on every engine.
 */

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

/** Value noise in [0,1). */
function valueNoise(x: number, y: number, scale: number, seed: number): number {
  const fx = x / scale;
  const fy = y / scale;
  const ix = Math.floor(fx);
  const iy = Math.floor(fy);
  const tx = smooth(fx - ix);
  const ty = smooth(fy - iy);
  const v = (a: number, b: number) => hash2(a, b, seed) / 4294967296;
  const a = v(ix, iy);
  const b = v(ix + 1, iy);
  const c = v(ix, iy + 1);
  const d = v(ix + 1, iy + 1);
  return a + (b - a) * tx + (c - a) * ty + (a - b - c + d) * tx * ty;
}

function fbm(x: number, y: number, scale: number, octaves: number, seed: number): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let s = scale;
  for (let o = 0; o < octaves; o++) {
    sum += valueNoise(x, y, s, seed + o * 1013) * amp;
    norm += amp;
    amp *= 0.5;
    s *= 0.5;
  }
  return sum / norm;
}

interface Profile {
  base: number;
  amp: number;
  scale: number;
  forest: number;
  forestScale: number;
  tree: string;
  sandBias: number;
  snow: boolean;
  edgeWater: number;
  cliffSlope: number;
}

const PROFILES: Record<MapType, Profile> = {
  continental: { base: 60, amp: 110, scale: 40, forest: 0.62, forestScale: 14, tree: 'oak', sandBias: 0, snow: false, edgeWater: 0.12, cliffSlope: 46 },
  highlands: { base: 70, amp: 260, scale: 32, forest: 0.64, forestScale: 14, tree: 'pine', sandBias: 0, snow: true, edgeWater: 0, cliffSlope: 40 },
  forest: { base: 60, amp: 90, scale: 40, forest: 0.5, forestScale: 10, tree: 'oak', sandBias: 0, snow: false, edgeWater: 0, cliffSlope: 46 },
  desert: { base: 60, amp: 100, scale: 36, forest: 0.74, forestScale: 10, tree: 'palm', sandBias: 1, snow: false, edgeWater: 0, cliffSlope: 46 },
  riverValley: { base: 70, amp: 110, scale: 40, forest: 0.6, forestScale: 13, tree: 'oak', sandBias: 0, snow: false, edgeWater: 0, cliffSlope: 46 },
  coastal: { base: 60, amp: 110, scale: 40, forest: 0.62, forestScale: 13, tree: 'oak', sandBias: 0, snow: false, edgeWater: 0, cliffSlope: 46 },
  mountainPass: { base: 60, amp: 90, scale: 40, forest: 0.63, forestScale: 13, tree: 'pine', sandBias: 0, snow: true, edgeWater: 0, cliffSlope: 46 },
  openPlains: { base: 60, amp: 50, scale: 48, forest: 0.76, forestScale: 12, tree: 'oak', sandBias: 0, snow: false, edgeWater: 0, cliffSlope: 46 },
  islands: { base: 60, amp: 80, scale: 30, forest: 0.62, forestScale: 12, tree: 'palm', sandBias: 0, snow: false, edgeWater: 0, cliffSlope: 46 },
  archipelago: { base: 60, amp: 80, scale: 24, forest: 0.62, forestScale: 10, tree: 'palm', sandBias: 0, snow: false, edgeWater: 0, cliffSlope: 46 },
};

export interface MapGenOptions {
  size: number;
  type: MapType;
  seed: number;
  players: number;
}

export function generateMap(opts: MapGenOptions): GameMap {
  const { size, type, seed, players } = opts;
  const prof = PROFILES[type];
  const rng = new Rng(seed ^ 0x5bd1e995);
  const vs = size + 1;
  const hf = new Float64Array(vs * vs);
  const c = size / 2;

  // Start positions on a ring, evenly spaced with a seeded rotation.
  const ringR = size * (prof.edgeWater > 0 || type === 'islands' ? 0.3 : type === 'coastal' ? 0.31 : 0.34);
  const rot = (rng.int(3600) / 3600) * 6.283185307179586;
  const starts: { tx: number; ty: number }[] = [];
  if (type === 'riverValley') {
    // Players alternate between the two banks, spread out along each bank, so the river (and its
    // single ford) always lies between opponents rather than under a town.
    const perBank = [Math.ceil(players / 2), Math.floor(players / 2)];
    const seen = [0, 0];
    for (let p = 0; p < players; p++) {
      const bank = p % 2;
      const n = perBank[bank];
      const k = seen[bank]++;
      const spread = n > 1 ? (k / (n - 1) - 0.5) * size * 0.5 : 0;
      starts.push({ tx: Math.round(c + spread), ty: Math.round(c + (bank ? 1 : -1) * ringR) });
    }
  } else {
    for (let p = 0; p < players; p++) {
      const ang = rot + (p * 6.283185307179586) / players;
      starts.push({ tx: Math.round(c + detCos(ang) * ringR), ty: Math.round(c + detSin(ang) * ringR) });
    }
  }

  // 1. Base height field.
  const fordHash = Math.imul(seed ^ 0x5bd1e995, 2654435761) >>> 0;
  const fordW = 4 + (fordHash % 3); // 4 to 6 tiles
  const fordX = Math.floor(size * 0.25) + ((fordHash >>> 8) % Math.max(1, Math.floor(size * 0.5) - fordW));
  for (let y = 0; y < vs; y++) {
    for (let x = 0; x < vs; x++) {
      const n = fbm(x, y, prof.scale, 4, seed);
      let h = prof.base + (n - 0.5) * 2 * prof.amp;
      // Inland lakes shelve steeply: a narrow wading rim, then water deep enough for ships. Seas
      // and coasts keep their gentle shelves.
      if (h < 0 && type !== 'islands' && type !== 'archipelago') h *= 5;
      const dx = (x - c) / c;
      const dy = (y - c) / c;
      const edge = Math.sqrt(dx * dx + dy * dy);

      if (prof.edgeWater > 0 && edge > 1 - prof.edgeWater * 2) {
        h -= (edge - (1 - prof.edgeWater * 2)) * 900;
      }
      if (type === 'coastal') {
        // Sea along one side, chosen by seed.
        const side = seed & 3;
        const t = side === 0 ? x / size : side === 1 ? 1 - x / size : side === 2 ? y / size : 1 - y / size;
        if (t < 0.18) h -= (0.18 - t) * 1600;
      }
      if (type === 'riverValley') {
        // Meandering river across the map, perpendicular to the player axis.
        const off = (fbm(x, 7, 30, 2, seed + 77) - 0.5) * size * 0.3;
        const d = Math.abs(y - c - off);
        if (d < 4) h = Math.min(h, -40 + d * 6);
        // One ford, placed by the seed somewhere along the middle half of the river and at most six
        // tiles wide, is shallow enough to wade: the only land route between the two banks.
        if (x >= fordX && x <= fordX + fordW) {
          if (d < 4) h = Math.max(h, -14);
          // Gentle approach ramps on both banks so the ford never ends at a cliff.
          else if (d < 14) h = Math.min(h, -14 + (d - 4) * 12);
        }
      }
      if (type === 'islands' || type === 'archipelago') {
        h -= 70;
      }
      hf[y * vs + x] = h;
    }
  }

  if (type === 'mountainPass' && players > 1) {
    // Ridges along the bisectors between consecutive players, with a pass in the middle.
    for (let p = 0; p < players; p++) {
      const ang = rot + ((p + 0.5) * 6.283185307179586) / players;
      const ux = detCos(ang);
      const uy = detSin(ang);
      for (let y = 0; y < vs; y++) {
        for (let x = 0; x < vs; x++) {
          const rx = x - c;
          const ry = y - c;
          const along = rx * ux + ry * uy;
          const perp = Math.abs(-rx * uy + ry * ux);
          if (along < size * 0.08 || perp > 4) continue;
          const passCentre = size * 0.27;
          if (Math.abs(along - passCentre) < 3.5) continue;
          hf[y * vs + x] += (4 - perp) * 90;
        }
      }
    }
  }

  if (type === 'islands' || type === 'archipelago') {
    // Raise land around each start and in scattered extra islands; link with shallow reefs.
    const blobs: { x: number; y: number; r: number }[] = starts.map((s) => ({ x: s.tx, y: s.ty, r: size * (type === 'islands' ? 0.17 : 0.12) }));
    const extra = type === 'archipelago' ? 6 + players * 2 : 2;
    for (let i = 0; i < extra; i++) blobs.push({ x: rng.range(10, size - 10), y: rng.range(10, size - 10), r: rng.range(5, 9) });
    for (let y = 0; y < vs; y++) {
      for (let x = 0; x < vs; x++) {
        let best = 0;
        for (const b of blobs) {
          const d = Math.sqrt((x - b.x) * (x - b.x) + (y - b.y) * (y - b.y));
          best = Math.max(best, 1 - d / b.r);
        }
        if (best > 0) hf[y * vs + x] += best * 260;
      }
    }
    // Shallow reefs between consecutive starts (until naval units are available).
    for (let p = 0; p < players; p++) {
      const a = starts[p];
      const b = starts[(p + 1) % players];
      const steps = Math.ceil(Math.sqrt((b.tx - a.tx) * (b.tx - a.tx) + (b.ty - a.ty) * (b.ty - a.ty)));
      for (let s = 0; s <= steps; s++) {
        const px = Math.round(a.tx + ((b.tx - a.tx) * s) / steps);
        const py = Math.round(a.ty + ((b.ty - a.ty) * s) / steps);
        for (let oy = -1; oy <= 2; oy++)
          for (let ox = -1; ox <= 2; ox++) {
            const qx = px + ox;
            const qy = py + oy;
            if (qx < 0 || qy < 0 || qx >= vs || qy >= vs) continue;
            const i = qy * vs + qx;
            if (hf[i] < -12) hf[i] = -12;
          }
      }
    }
  }

  // Flatten start plateaus so towns sit on buildable ground.
  for (const s of starts) {
    const target = Math.max(40, hf[s.ty * vs + s.tx]);
    const plateau = Math.min(target, 140);
    for (let y = s.ty - 13; y <= s.ty + 13; y++) {
      for (let x = s.tx - 13; x <= s.tx + 13; x++) {
        if (x < 0 || y < 0 || x >= vs || y >= vs) continue;
        const d = Math.sqrt((x - s.tx) * (x - s.tx) + (y - s.ty) * (y - s.ty));
        const w = d < 9 ? 1 : d < 13 ? 1 - (d - 9) / 4 : 0;
        const i = y * vs + x;
        hf[i] = hf[i] * (1 - w) + plateau * w;
      }
    }
  }

  // Quantise.
  const heights = new Int16Array(vs * vs);
  for (let i = 0; i < hf.length; i++) heights[i] = Math.max(-300, Math.min(1200, Math.round(hf[i])));

  // 2. Classify terrain per tile.
  const terrain = new Uint8Array(size * size);
  const passable = new Uint8Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const h00 = heights[y * vs + x];
      const h10 = heights[y * vs + x + 1];
      const h01 = heights[(y + 1) * vs + x];
      const h11 = heights[(y + 1) * vs + x + 1];
      const hmin = Math.min(h00, h10, h01, h11);
      const hmax = Math.max(h00, h10, h01, h11);
      const havg = (h00 + h10 + h01 + h11) / 4;
      const slope = hmax - hmin;
      const moist = fbm(x, y, 18, 3, seed + 991);
      let t: Terrain;
      if (havg < -25) t = Terrain.Water;
      else if (havg < 0) t = Terrain.Shallow;
      else if (havg < 14) t = Terrain.Sand;
      else if (slope > prof.cliffSlope) t = Terrain.Rock;
      else if (prof.snow && havg > 300) t = Terrain.Snow;
      else if (prof.sandBias > 0) t = moist > 0.62 ? Terrain.Grass : moist > 0.5 ? Terrain.Dirt : Terrain.Sand;
      else t = moist < 0.33 ? Terrain.Dirt : Terrain.Grass;
      terrain[y * size + x] = t;
      passable[y * size + x] = t === Terrain.Water || (t === Terrain.Rock && slope > prof.cliffSlope) ? 0 : 1;
    }
  }

  // Roads: worn tracks from each town towards the map centre (cosmetic + slightly faster travel later).
  for (const st of starts) {
    const steps = Math.ceil(Math.sqrt((c - st.tx) * (c - st.tx) + (c - st.ty) * (c - st.ty)));
    for (let k = 3; k < steps - 4; k++) {
      const f = k / steps;
      const wob = (fbm(k, st.tx, 9, 2, seed + 31) - 0.5) * 6;
      const px = Math.round(st.tx + (c - st.tx) * f + (-(c - st.ty) / steps) * wob);
      const py = Math.round(st.ty + (c - st.ty) * f + ((c - st.tx) / steps) * wob);
      for (const [ox, oy] of [[0, 0], [1, 0], [0, 1]]) {
        const x = px + ox;
        const y = py + oy;
        if (x < 0 || y < 0 || x >= size || y >= size) continue;
        const i = y * size + x;
        if (terrain[i] === Terrain.Grass || terrain[i] === Terrain.Dirt || terrain[i] === Terrain.Sand) terrain[i] = Terrain.Road;
      }
    }
  }

  // 3. Resources.
  const resources: ResourceSpawn[] = [];
  const occupied = new Uint8Array(size * size);
  const free = (tx: number, ty: number, margin = 0): boolean => {
    for (let y = ty - margin; y <= ty + margin; y++)
      for (let x = tx - margin; x <= tx + margin; x++) {
        if (x < 1 || y < 1 || x >= size - 1 || y >= size - 1) return false;
        const i = y * size + x;
        if (!passable[i] || occupied[i] || terrain[i] === Terrain.Shallow) return false;
      }
    return true;
  };
  const put = (k: string, tx: number, ty: number): void => {
    resources.push({ kind: k, tx, ty });
    occupied[ty * size + tx] = 1;
  };
  // Reserve town centre area.
  for (const s of starts)
    for (let y = s.ty - 4; y <= s.ty + 4; y++) for (let x = s.tx - 4; x <= s.tx + 4; x++) if (x >= 0 && y >= 0 && x < size && y < size) occupied[y * size + x] = 2;

  const spiralFind = (cx: number, cy: number, margin: number): { tx: number; ty: number } | null => {
    for (let r = 0; r < 12; r++)
      for (let dy = -r; dy <= r; dy++)
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          if (free(cx + dx, cy + dy, margin)) return { tx: cx + dx, ty: cy + dy };
        }
    return null;
  };

  const cluster = (k: string, cx: number, cy: number, count: number, spread: number): void => {
    const p = spiralFind(cx, cy, 1);
    if (!p) return;
    let placed = 0;
    const r2 = new Rng(hash2(cx, cy, seed));
    for (let attempt = 0; attempt < count * 8 && placed < count; attempt++) {
      const tx = p.tx + r2.range(-spread, spread);
      const ty = p.ty + r2.range(-spread, spread);
      if (placed === 0 || free(tx, ty)) {
        if (!free(tx, ty)) continue;
        put(k, tx, ty);
        placed++;
      }
    }
  };

  // Per-player resources at identical relative offsets (rotated toward map centre) for fairness.
  const tree = prof.tree;
  for (const s of starts) {
    let ux = c - s.tx;
    let uy = c - s.ty;
    const len = Math.sqrt(ux * ux + uy * uy) || 1;
    ux /= len;
    uy /= len;
    const at = (f: number, side: number) => ({ x: Math.round(s.tx + ux * f - uy * side), y: Math.round(s.ty + uy * f + ux * side) });
    // Livestock grazing beside the town centre.
    for (const [f, side] of [[4, 2], [4, -2], [5, 0], [-4, 3], [-4, -3], [3, 4], [3, -4], [-5, 0]]) {
      const q = at(f, side);
      if (free(q.x, q.y)) put('sheep', q.x, q.y);
    }
    const g1 = at(2, 8);
    cluster('goldMine', g1.x, g1.y, 6, 1);
    const st = at(-1, -8);
    cluster('stoneMine', st.x, st.y, 5, 1);
    const be = at(6, -3);
    cluster('berries', be.x, be.y, 6, 1);
    const g2 = at(-10, 4);
    cluster('goldMine', g2.x, g2.y, 5, 1);
    const fo = at(-9, -5);
    cluster(tree, fo.x, fo.y, 26, 3);
    const fo2 = at(3, 11);
    cluster(tree, fo2.x, fo2.y, 18, 2);
    const de = at(9, 7);
    cluster('deer', de.x, de.y, 4, 2);
    const de2 = at(-6, 10);
    cluster('deer', de2.x, de2.y, 3, 2);
    // Expansion gold further out.
    const ex = at(15, 10);
    cluster('goldMine', ex.x, ex.y, 6, 1);
    const ex2 = at(14, -12);
    cluster('stoneMine', ex2.x, ex2.y, 4, 1);
  }

  // Global forests from noise, away from start areas.
  const forestFloor = new Uint8Array(size * size);
  for (let y = 2; y < size - 2; y++) {
    for (let x = 2; x < size - 2; x++) {
      const i = y * size + x;
      if (!passable[i] || occupied[i] || terrain[i] === Terrain.Sand || terrain[i] === Terrain.Shallow) continue;
      let near = false;
      for (const s of starts) if ((x - s.tx) * (x - s.tx) + (y - s.ty) * (y - s.ty) < 12 * 12) near = true;
      if (near) continue;
      const n = fbm(x, y, prof.forestScale, 3, seed + 4242);
      if (n > prof.forest) {
        const h = hash2(x, y, seed + 5);
        if ((h & 7) < 6) {
          const k = prof.snow && heights[y * vs + x] > 220 ? 'pine' : (h >> 8) % 5 === 0 && tree === 'oak' ? 'pine' : tree;
          put(k, x, y);
          forestFloor[i] = 1;
        }
      }
    }
  }
  // Desert oases: palms near shallow water.
  if (type === 'desert') {
    for (let y = 2; y < size - 2; y++)
      for (let x = 2; x < size - 2; x++) {
        const i = y * size + x;
        if (terrain[i] !== Terrain.Sand || occupied[i]) continue;
        const nearWater = terrain[i - 1] >= Terrain.Shallow || terrain[i + 1] >= Terrain.Shallow || terrain[i - size] >= Terrain.Shallow || terrain[i + size] >= Terrain.Shallow;
        if (nearWater && (hash2(x, y, seed + 9) & 3) === 0) put('palm', x, y);
      }
  }
  for (let i = 0; i < forestFloor.length; i++) if (forestFloor[i] && terrain[i] === Terrain.Grass) terrain[i] = Terrain.Forest;

  // Fish: shoals in water near each start (if any water is close), plus scattered deep-water fish.
  const isWater = (i: number) => terrain[i] === Terrain.Water || terrain[i] === Terrain.Shallow;
  const fishTaken = new Uint8Array(size * size);
  const putFish = (x: number, y: number) => {
    resources.push({ kind: 'fish', tx: x, ty: y });
    for (let yy = y - 1; yy <= y + 1; yy++) for (let xx = x - 1; xx <= x + 1; xx++) if (xx >= 0 && yy >= 0 && xx < size && yy < size) fishTaken[yy * size + xx] = 1;
  };
  for (const st of starts) {
    const cand: [number, number, number][] = [];
    for (let y = Math.max(1, st.ty - 20); y < Math.min(size - 1, st.ty + 20); y++)
      for (let x = Math.max(1, st.tx - 20); x < Math.min(size - 1, st.tx + 20); x++) {
        const d = (x - st.tx) * (x - st.tx) + (y - st.ty) * (y - st.ty);
        if (d < 64 || d > 400 || !isWater(y * size + x)) continue;
        // Prefer tiles a little off the shore.
        let water = 0;
        for (let k = 0; k < 9; k++) if (isWater((y + ((k / 3) | 0) - 1) * size + x + (k % 3) - 1)) water++;
        if (water >= 8) cand.push([d, x, y]);
      }
    cand.sort((a, b) => a[0] - b[0] || a[1] - b[1] || a[2] - b[2]);
    let n = 0;
    for (const [, x, y] of cand) {
      if (n >= 4) break;
      if (fishTaken[y * size + x]) continue;
      putFish(x, y);
      n++;
    }
  }
  for (let y = 2; y < size - 2; y += 3)
    for (let x = 2; x < size - 2; x += 3) {
      const i = y * size + x;
      if (terrain[i] !== Terrain.Water || fishTaken[i]) continue;
      if ((hash2(x, y, seed + 77) & 31) === 0) putFish(x, y);
    }

  // Neutral central resources.
  const extraGold = Math.max(2, players);
  for (let i = 0; i < extraGold; i++) {
    const ang = rot + ((i + 0.5) * 6.283185307179586) / extraGold;
    cluster('goldMine', Math.round(c + detCos(ang) * size * 0.12), Math.round(c + detSin(ang) * size * 0.12), 5, 1);
  }

  return { size, type, seed, heights, terrain, passable, starts, resources: withinBudget(resources, starts, prof.tree, seed) };
}

/**
 * Keeps a map's resources within RESOURCE_BUDGET by removing trees, the ones furthest from every
 * start first in an order set by the seed, so each player's own woodline stays whole. Maps already
 * within the budget are returned unchanged.
 */
function withinBudget(resources: ResourceSpawn[], starts: { tx: number; ty: number }[], tree: string, seed: number): ResourceSpawn[] {
  const excess = resources.length - RESOURCE_BUDGET;
  if (excess <= 0) return resources;
  const near = (r: ResourceSpawn) => starts.some((s) => (r.tx - s.tx) ** 2 + (r.ty - s.ty) ** 2 < 16 * 16);
  const trees = resources
    .map((r, i) => ({ i, near: near(r) ? 1 : 0, h: hash2(r.tx, r.ty, seed + 991) }))
    .filter((t) => resources[t.i].kind === tree)
    .sort((a, b) => a.near - b.near || a.h - b.h || a.i - b.i);
  const drop = new Set(trees.slice(0, excess).map((t) => t.i));
  return resources.filter((_, i) => !drop.has(i));
}
